import React, { useState } from 'react';
import { KatIcon, KatPopover } from '@amzn/katal-react';
import { RenderElementProps, useSlate } from 'slate-react';
import { Editor, Element, Transforms } from 'slate';
import { ElementTypes, LinkElement } from 'src/types/CustomSlateTypes';
import AddLinkModal, { AddLinkModalProps } from './AddLinkModal';
import createUrl from './createUrl';

export type LinkProps = {
  element: LinkElement;
} & RenderElementProps;

const Link = ({ element, children, attributes }: LinkProps) => {
  const editor = useSlate();
  const [showModal, setShowModal] = useState(false);
  const { url } = element as LinkElement;

  const handleSave: AddLinkModalProps['onSave'] = (values) => {
    Transforms.removeNodes(editor, {
      match: (n) => !Editor.isEditor(n)
        && Element.isElement(n)
        && n.type === ElementTypes.link,
    });

    const { text: newText } = values;
    const newUrl = createUrl(values.url);
    const link: LinkElement = {
      type: ElementTypes.link,
      url: newUrl,
      children: [{ text: newText || newUrl }],
    };

    Transforms.insertNodes(editor, link);
  };

  const handleDelete = () => {
    Transforms.unwrapNodes(editor, {
      match: (n) => !Editor.isEditor(n)
        && Element.isElement(n)
        && n.type === ElementTypes.link,
    });
  };

  return (
    <>
      <KatPopover
        className="inline-flex whitespace-normal"
        katAriaBehavior="tooltip"
        position="top"
        triggerType="click"
        {...attributes}
      >
        <a href={url} className="whitespace-pre-wrap" slot="trigger">{children}</a>
        <span contentEditable={false} className="flex space-x-2 break-all rtl:space-x-reverse">
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
          <button
            data-testid="edit-button"
            type="button"
            className="cursor-pointer border-0 bg-kat-white px-0 pt-1 pb-0"
            onClick={() => { setShowModal(true); }}
          >
            <KatIcon name="edit" size="tiny" />
          </button>
          <button
            data-testid="delete-button"
            type="button"
            className="cursor-pointer border-0 bg-kat-white px-0 pt-1 pb-0"
            onClick={handleDelete}
          >
            <KatIcon name="trash" size="tiny" />
          </button>
        </span>
      </KatPopover>
      {showModal && (
        <AddLinkModal
          onClose={() => { setShowModal(false); }}
          onSave={handleSave}
          linkElement={element}
        />
      )}
    </>
  );
};

export default Link;
